import React, { useState, useMemo } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Box,
  TextField,
  CircularProgress,
  Typography,
  Button
} from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import { makeStyles } from '@material-ui/core/styles';
import { Refresh, Error as ErrorIcon, CheckCircle, Cancel, GetApp } from '@material-ui/icons';
import { useMezzanine } from '../../context/MezzanineContext';
import * as XLSX from 'xlsx';

const useStyles = makeStyles((theme) => ({
  container: {
    padding: theme.spacing(2),
  },
  tableCell: {
    padding: '10px 16px',
    fontSize: '14px',
  },
  headerCell: {
    padding: '8px 16px',
    backgroundColor: 'white',
  },
  autocomplete: {
    '& .MuiOutlinedInput-root': {
      padding: '2px 8px',
      fontSize: '14px',
      backgroundColor: 'white',
      '& fieldset': {
        borderColor: '#ccc',
      },
      '&:hover fieldset': {
        borderColor: '#999',
      },
      '&.Mui-focused fieldset': {
        borderColor: '#ff6f00',
      },
    },
    '& .MuiAutocomplete-input': {
      padding: '4px 4px',
      fontSize: '14px',
    },
  },
}));

const TxChains = () => {
  const classes = useStyles();
  const { txChains, txChainsLoading, txChainsError, fetchTxChains } = useMezzanine();
  const [filters, setFilters] = useState({
    data_center: 'all',
    tx_chain_name: 'all',
    device_id: 'all',
    available: 'all',
    channel_name: ''
  });

  const getUniqueValues = (field) => {
    if (field === 'available') {
      return ['true', 'false'];
    }
    if (field === 'channel_name') {
      return [...new Set(txChains.map(item => item[field]).filter(Boolean))].sort();
    }
    return [...new Set(txChains.map(item => item[field]).filter(Boolean))].sort();
  };

  const handleFilterChange = (field, value) => {
    setFilters({ ...filters, [field]: value || 'all' });
  };

  const filteredData = useMemo(() => {
    return txChains.filter(chain => {
      const dcMatch = filters.data_center === 'all' || chain.data_center === filters.data_center;
      const nameMatch = filters.tx_chain_name === 'all' || chain.tx_chain_name === filters.tx_chain_name;
      const deviceMatch = filters.device_id === 'all' || chain.device_id === filters.device_id;
      const availMatch = filters.available === 'all' || String(chain.available) === filters.available;
      const channelMatch = !filters.channel_name || (chain.channel_name && chain.channel_name.toLowerCase().includes(filters.channel_name.toLowerCase()));
      return dcMatch && nameMatch && deviceMatch && availMatch && channelMatch;
    });
  }, [txChains, filters]);

  const handleExport = () => {
    const exportData = filteredData.map(chain => ({
      'Data Center': chain.data_center,
      'TX Chain': chain.tx_chain_name,
      'Device ID': chain.device_id,
      'Slot': chain.slot,
      'SDI': chain.sdi,
      'Available': chain.available ? 'Yes' : 'No',
      'UDP Multicast': chain.udp_multicast_ip || '',
      'Channel': chain.channel_name || ''
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'TX Chains');
    XLSX.writeFile(workbook, `tx_chains_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  if (txChainsLoading && txChains.length === 0) {
    return (
      <Box className={classes.container} display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress style={{ color: '#ff6f00' }} />
      </Box>
    );
  }

  if (txChainsError && txChains.length === 0) {
    return (
      <Box className={classes.container}>
        <Paper style={{ padding: '20px', backgroundColor: '#ffebee', textAlign: 'center' }}>
          <ErrorIcon style={{ color: '#c62828', fontSize: '48px' }} />
          <Typography variant="h6" style={{ color: '#c62828', marginTop: '10px' }}>
            Failed to load TX chains
          </Typography>
          <Typography variant="body2" style={{ color: '#c62828', marginBottom: '15px' }}>
            {txChainsError}
          </Typography>
          <IconButton onClick={fetchTxChains} style={{ color: '#c62828', border: '2px solid #c62828' }}>
            <Refresh />
          </IconButton>
        </Paper>
      </Box>
    );
  }

  return (
    <Box className={classes.container}>
      {/* Export and Refresh Buttons */}
      <Box display="flex" justifyContent="flex-end" alignItems="center" style={{ marginBottom: '16px', gap: '12px' }}>
        <Button
          variant="contained"
          startIcon={<GetApp />}
          onClick={handleExport}
          disabled={filteredData.length === 0}
          style={{ backgroundColor: '#ff6f00', color: 'white' }}
        >
          Export
        </Button>
        <IconButton
          onClick={fetchTxChains}
          disabled={txChainsLoading}
          size="small"
          style={{ color: '#ff6f00', border: '1px solid #ff6f00', borderRadius: '4px', padding: '6px' }}
        >
          {txChainsLoading ? <CircularProgress size={20} style={{ color: '#ff6f00' }} /> : <Refresh fontSize="small" />}
        </IconButton>
      </Box>

      {/* Table */}
      <TableContainer component={Paper} style={{ borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
        <Table size="small">
          <TableHead>
            <TableRow style={{ backgroundColor: 'white' }}>
              <TableCell className={classes.headerCell} style={{ width: '15%' }}>
                <Autocomplete
                  className={classes.autocomplete}
                  options={getUniqueValues('data_center')}
                  value={filters.data_center === 'all' ? null : filters.data_center}
                  onChange={(e, newValue) => handleFilterChange('data_center', newValue)}
                  size="small"
                  renderInput={(params) => (
                    <TextField {...params} variant="outlined" placeholder="Data Center" />
                  )}
                />
              </TableCell>
              <TableCell className={classes.headerCell} style={{ width: '12%' }}>
                <Autocomplete
                  className={classes.autocomplete}
                  options={getUniqueValues('tx_chain_name')}
                  value={filters.tx_chain_name === 'all' ? null : filters.tx_chain_name}
                  onChange={(e, newValue) => handleFilterChange('tx_chain_name', newValue)}
                  size="small"
                  renderInput={(params) => (
                    <TextField {...params} variant="outlined" placeholder="TX Chain" />
                  )}
                />
              </TableCell>
              <TableCell className={classes.headerCell} style={{ width: '15%' }}>
                <Autocomplete
                  className={classes.autocomplete}
                  options={getUniqueValues('device_id')}
                  value={filters.device_id === 'all' ? null : filters.device_id}
                  onChange={(e, newValue) => handleFilterChange('device_id', newValue)}
                  size="small"
                  renderInput={(params) => (
                    <TextField {...params} variant="outlined" placeholder="Device ID" />
                  )}
                />
              </TableCell>
              <TableCell className={classes.headerCell} align="center" style={{ width: '8%' }}>
                <strong style={{ fontSize: '14px' }}>Slot</strong>
              </TableCell>
              <TableCell className={classes.headerCell} align="center" style={{ width: '10%' }}>
                <strong style={{ fontSize: '14px' }}>SDI</strong>
              </TableCell>
              <TableCell className={classes.headerCell} style={{ width: '10%' }}>
                <Autocomplete
                  className={classes.autocomplete}
                  options={getUniqueValues('available')}
                  value={filters.available === 'all' ? null : filters.available}
                  onChange={(e, newValue) => handleFilterChange('available', newValue)}
                  size="small"
                  renderInput={(params) => (
                    <TextField {...params} variant="outlined" placeholder="Available" />
                  )}
                />
              </TableCell>
              <TableCell className={classes.headerCell} style={{ width: '14%' }}>
                <strong style={{ fontSize: '14px' }}>UDP Multicast</strong>
              </TableCell>
              <TableCell className={classes.headerCell} style={{ width: '16%' }}>
                <TextField
                  variant="outlined"
                  size="small"
                  placeholder="Channel"
                  value={filters.channel_name}
                  onChange={(e) => setFilters({ ...filters, channel_name: e.target.value })}
                  style={{ width: '100%' }}
                  InputProps={{
                    style: { fontSize: '14px', backgroundColor: 'white' }
                  }}
                />
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredData.map((chain, index) => (
              <TableRow
                key={`${chain.data_center}-${chain.tx_chain_name}-${index}`}
                hover
                style={{
                  backgroundColor: '#fff8f0',
                  borderBottom: '8px solid #fff'
                }}
              >
                <TableCell className={classes.tableCell}>{chain.data_center}</TableCell>
                <TableCell className={classes.tableCell} style={{ fontWeight: 'bold' }}>{chain.tx_chain_name}</TableCell>
                <TableCell className={classes.tableCell}>{chain.device_id}</TableCell>
                <TableCell className={classes.tableCell} align="center">{chain.slot}</TableCell>
                <TableCell className={classes.tableCell} align="center">{chain.sdi}</TableCell>
                <TableCell className={classes.tableCell} align="center">
                  {chain.available ? (
                    <CheckCircle style={{ color: '#4caf50', fontSize: '20px' }} />
                  ) : (
                    <Cancel style={{ color: '#f44336', fontSize: '20px' }} />
                  )}
                </TableCell>
                <TableCell className={classes.tableCell}>
                  {chain.udp_multicast_ip || '-'}
                </TableCell>
                <TableCell className={classes.tableCell}>
                  {chain.channel_name || '-'}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default TxChains;
